import { useState, useCallback, useEffect } from 'react';
import {
	ErrorOutline,
	DoneAll,
	DateRange,
	Edit,
	ChevronLeft,
	ChevronRight,
	Add,
} from '@mui/icons-material';
import { Button, useToast, useConfirmModal, ConfirmModal } from '@practium/ui';
import { getApiErrorMessage } from '@practium/features/api';
import type { FieldDiaryExportFormat } from '@practium/features/api';
import type { FieldDiaryEntry, FieldDiaryFarm } from '../types';
import { useFieldDiary } from '../hooks/useFieldDiary';
import { useInsertionFlow } from '../hooks/useInsertionFlow';
import { FarmSelectionModal } from '../components/FarmModal/FarmSelectionModal';
import { StatCard } from '../components/Cards/StatCard';
import { FieldDiaryCalendar } from '../components/Calendar/FieldDiaryCalendar';
import { InsertionList } from '../components/InsertionList/InsertionList';
import { useNavigate } from 'react-router';
import { NoteModal } from '../components/Modals/NoteModal/NoteModal';
import styles from './FieldDiaryPage.module.css';

export interface FieldDiaryPageProps {
	farms: FieldDiaryFarm[];
	farmsLoading?: boolean;
}

export const FieldDiaryPage = ({ farms, farmsLoading = false }: FieldDiaryPageProps) => {
	const navigate = useNavigate();
	const [selectedFarm, setSelectedFarm] = useState<FieldDiaryFarm | null>(null);
	const [farmModalOpen, setFarmModalOpen] = useState(true);

	const { success: toastSuccess, error: toastError } = useToast();
	const {
		confirm,
		isOpen: isConfirmOpen,
		options: confirmOptions,
		handleConfirm,
		handleCancel: handleConfirmCancel,
	} = useConfirmModal();

	const insertion = useInsertionFlow();

	const handleSelectFarm = useCallback((farm: FieldDiaryFarm) => {
		setSelectedFarm(farm);
		setFarmModalOpen(false);
	}, []);

	const handleCloseFarmModal = useCallback(() => {
		if (selectedFarm) {
			setFarmModalOpen(false);
		} else {
			navigate(-1);
		}
	}, [navigate, selectedFarm]);

	const handleEntryClick = insertion.openViewEntry;

	const {
		monthLabel,
		canGoNext,
		goToPrevMonth,
		goToNextMonth,
		entries,
		entriesByDay,
		stats,
		calendarDays,
		selectedDay,
		setSelectedDay,
		isToday,
		loadError,
		saveNote,
		deleteNote,
		isDeleting,
		exportDiary,
		isExporting,
		isSummaryLoading,
		isEntriesLoading,
	} = useFieldDiary({ propId: selectedFarm?._id ?? '', forBinding: selectedFarm?.ownerId });

	useEffect(() => {
		if (loadError) {
			toastError(getApiErrorMessage(loadError, 'Não foi possível carregar o diário de campo.'));
		}
	}, [loadError, toastError]);

	const capitalizedMonth = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);

	const handleDeleteEntry = useCallback(
		async (entry: FieldDiaryEntry) => {
			if (isDeleting) return;
			const confirmed = await confirm({
				title: 'Excluir nota',
				message: 'Tem certeza que deseja excluir esta nota? Esta ação não pode ser desfeita.',
				confirmText: 'Excluir',
				cancelText: 'Cancelar',
				variant: 'danger',
			});
			if (!confirmed) return;
			try {
				await deleteNote(entry._id);
				toastSuccess('Nota excluída com sucesso.');
				insertion.close();
			} catch (err) {
				toastError(getApiErrorMessage(err, 'Erro ao excluir nota'));
			}
		},
		[confirm, deleteNote, isDeleting, toastSuccess, toastError, insertion]
	);

	const handleExport = useCallback(
		async (format: FieldDiaryExportFormat) => {
			if (isExporting) return;
			try {
				await exportDiary(format);
			} catch (err) {
				toastError(getApiErrorMessage(err, 'Erro ao exportar o diário de campo'));
			}
		},
		[exportDiary, isExporting, toastError]
	);

	return (
		<>
			<FarmSelectionModal
				isOpen={farmModalOpen || !selectedFarm}
				onClose={handleCloseFarmModal}
				onSelect={handleSelectFarm}
				farms={farms}
				loading={farmsLoading}
			/>

			<NoteModal
				isOpen={insertion.isNoteOpen}
				monthLabel={capitalizedMonth}
				onClose={insertion.close}
				onSave={saveNote}
				onDelete={handleDeleteEntry}
			/>

			<NoteModal
				isOpen={insertion.isViewNoteOpen}
				monthLabel={capitalizedMonth}
				initialMode="view"
				viewEntry={insertion.selectedEntry ?? undefined}
				onClose={insertion.close}
				onSave={saveNote}
				onDelete={handleDeleteEntry}
			/>

			<ConfirmModal
				isOpen={isConfirmOpen}
				title={confirmOptions.title}
				message={confirmOptions.message}
				confirmText={confirmOptions.confirmText}
				cancelText={confirmOptions.cancelText}
				variant={confirmOptions.variant}
				onConfirm={handleConfirm}
				onCancel={handleConfirmCancel}
			/>

			{selectedFarm && (
				<div className={styles.page}>
					<div className={styles.topBar}>
						<div className={styles.monthNav}>
							<button
								type="button"
								className={styles.navBtn}
								onClick={goToPrevMonth}
								aria-label="Mês anterior"
							>
								<ChevronLeft className={styles.navIcon} />
							</button>
							<span className={styles.monthLabel}>{capitalizedMonth}</span>
							<button
								type="button"
								className={`${styles.navBtn} ${canGoNext ? '' : styles.navBtnDisabled}`}
								onClick={goToNextMonth}
								disabled={!canGoNext}
								aria-label="Próximo mês"
							>
								<ChevronRight
									className={`${styles.navIcon} ${canGoNext ? '' : styles.navIconDisabled}`}
								/>
							</button>
						</div>

						<div className={styles.buttonWrapper}>
							<Button
								variant="primary"
								className={styles.newEntryBtn}
								icon={<Add className={styles.btnIcon} />}
								onClick={insertion.open}
								type="button"
							>
								Nova Inserção
							</Button>
						</div>
					</div>

					<div className={styles.statsRow}>
						<StatCard
							title="PENDENTES"
							value={stats.pending}
							label={stats.pendingLabel}
							icon={<ErrorOutline className={styles.statCardIcon} />}
							variant="pending"
							isLoading={isSummaryLoading}
						/>
						<StatCard
							title="REALIZADAS"
							value={stats.done}
							label={stats.doneLabel}
							icon={<DoneAll className={styles.statCardIcon} />}
							variant="done"
							isLoading={isSummaryLoading}
						/>
						<StatCard
							title="A VENCER"
							value={stats.upcoming}
							label={stats.upcomingLabel}
							icon={<DateRange className={styles.statCardIcon} />}
							variant="upcoming"
							isLoading={isSummaryLoading}
						/>
						<StatCard
							title="MINHAS NOTAS"
							value={stats.notes}
							label={stats.notesLabel}
							icon={<Edit className={styles.statCardIcon} />}
							variant="note"
							isLoading={isSummaryLoading}
						/>
					</div>

					<div className={styles.mainContent}>
						<div className={styles.calendarWrapper}>
							<FieldDiaryCalendar
								farmName={selectedFarm.name}
								calendarDays={calendarDays}
								entriesByDay={entriesByDay}
								selectedDay={selectedDay}
								onSelectDay={setSelectedDay}
								isToday={isToday}
								onEntryClick={handleEntryClick}
							/>
						</div>
						<div className={styles.listWrapper}>
							<InsertionList
								entries={entries}
								isLoading={isEntriesLoading}
								onEntryClick={handleEntryClick}
								onExportExcel={() => handleExport('xlsx')}
								onExportPdf={() => handleExport('pdf')}
							/>
						</div>
					</div>
				</div>
			)}
		</>
	);
};
